import { useNavigate } from "react-router-dom";
import { Element } from "@nkitaa/builder-engine";
import { IconMail, IconPhone, IconMapPin } from "@tabler/icons-react";
import {
  resolveBodyColor,
  resolveHeadingColor,
  resolveHeadingFontFamily,
  resolveHeadingFontSize,
  resolveHyperlinkFontColor,
  resolveHyperlinkHoverColor,
  resolveHyperlinkUnderline,
} from "../theme/themeValueResolver";
import { useWBTheme } from "../theme/WBThemeRuntimeProvider";
import { useWBLang } from "../lang/WBLanguageRuntimeProvider";
import { resolveText, type LangText } from "../lib/resolve-text";
import {
  type BorderSide,
  type ParentFilter,
  type RuntimeProps,
  classNames,
  extractUtilityClasses,
  getBorderClasses,
  isVisible,
  nonEmptyString,
  sectionMinHeight,
  toCssLength,
} from "./shared";
import { AppBarLogo, filterVisibleLinks, iconForPlatform, linkClick, normalizeSocialIcons, useLinkVisibilityUser } from "./appBar";
// PR-A4 / H9 — same allow-list wrap as appBar.tsx for footer hrefs.
import { safeHref } from "../lib/safe-href";

export function FooterLinks({
  isVisible = true,
  linkGroups,
  titleColor,
  titleFontSize,
  titleFontWeight,
  linksColor,
  linksFontSize,
  linksFontWeight,
  linksHoverColor,
}: {
  isVisible?: boolean;
  linkGroups?: Array<{
    title: LangText;
    links: Array<{
      label: LangText;
      url: string;
      linkType?: "internal" | "external";
      isVisible?: boolean;
      visibilityFilter?: ParentFilter;
    }>;
  }>;
  titleColor?: string;
  titleFontSize?: string;
  titleFontWeight?: string;
  linksColor?: string;
  linksFontSize?: string;
  linksFontWeight?: string;
  linksHoverColor?: string;
}) {
  const navigate = useNavigate();
  const { currentColorPalette, currentFonts, headingsTheme, hyperlinkTheme } =
    useWBTheme();
  const { currentLang, primaryLanguage } = useWBLang();
  const visibilityUser = useLinkVisibilityUser();
  if (!isVisible) return null;

  const linkColor = resolveHyperlinkFontColor({
    override: linksColor,
    hyperlinkTheme,
    palette: currentColorPalette,
    allowInherit: false,
  });
  const hoverLinkColor = resolveHyperlinkHoverColor({
    override: linksHoverColor,
    hyperlinkTheme,
    palette: currentColorPalette,
  });
  const underline = resolveHyperlinkUnderline(hyperlinkTheme);
  const resolvedTitleColor = resolveHeadingColor({
    override: titleColor,
    level: "h2",
    headingsTheme,
    palette: currentColorPalette,
  });
  const resolvedTitleFontFamily = resolveHeadingFontFamily({
    override: undefined,
    level: "h2",
    headingsTheme,
    fonts: currentFonts,
  });
  const resolvedTitleFontSize = resolveHeadingFontSize({
    override: titleFontSize,
    level: "h2",
    headingsTheme,
  });
  const resolvedLinkGroups = linkGroups ?? [];
  const titleSizeClasses = extractUtilityClasses(titleFontSize, "text-");
  const titleWeightClasses = extractUtilityClasses(titleFontWeight, "font-");
  const linksSizeClasses = extractUtilityClasses(linksFontSize, "text-");
  const linksWeightClasses = extractUtilityClasses(linksFontWeight, "font-");

  return (
    <div className="flex flex-wrap gap-x-10 gap-y-8">
      {resolvedLinkGroups.map((group, idx) => (
        <div key={idx} className="min-w-35">
          <h3
            className={`${titleWeightClasses || "font-semibold"} mb-4`}
            style={{
              color: resolvedTitleColor,
              fontFamily: resolvedTitleFontFamily,
              fontSize: titleSizeClasses
                ? undefined
                : (toCssLength(titleFontSize) ?? resolvedTitleFontSize),
            }}
          >
            {resolveText(group.title, currentLang, primaryLanguage)}
          </h3>
          <ul className="space-y-2">
            {filterVisibleLinks(group.links, visibilityUser).map((link, linkIdx) => (
              <li key={`${link.url}-${linkIdx}`}>
                <a
                  className={classNames(
                    linksSizeClasses || undefined,
                    linksWeightClasses || undefined,
                    "transition-colors duration-200 inline-block",
                    underline ? "underline" : "no-underline",
                    "hover:underline",
                  )}
                  href={safeHref(link.url)}
                  target={link.linkType === "external" ? "_blank" : undefined}
                  rel={
                    link.linkType === "external"
                      ? "noopener noreferrer"
                      : undefined
                  }
                  onClick={(event) =>
                    linkClick(navigate, event, link.url, link.linkType)
                  }
                  style={{
                    color: linkColor,
                    fontSize: toCssLength(linksFontSize),
                  }}
                  onMouseEnter={(event) => {
                    event.currentTarget.style.color = hoverLinkColor;
                  }}
                  onMouseLeave={(event) => {
                    event.currentTarget.style.color = linkColor;
                  }}
                >
                  {resolveText(link.label, currentLang, primaryLanguage)}
                </a>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

export function FooterSocial({
  isVisible = true,
  socialIcons,
  title,
  color,
  size,
  titleColor,
  titleFontSize,
  titleFontWeight,
}: {
  isVisible?: boolean;
  socialIcons?: Array<{ platform: string; url: string }>;
  title?: string;
  color?: string;
  size?: number;
  titleColor?: string;
  titleFontSize?: string;
  titleFontWeight?: string;
}) {
  const { currentFonts, currentColorPalette, bodyTheme, headingsTheme } =
    useWBTheme();
  const resolvedTitleColor = resolveHeadingColor({
    override: titleColor,
    level: "h2",
    headingsTheme,
    palette: currentColorPalette,
  });
  const resolvedTitleFontFamily = resolveHeadingFontFamily({
    override: undefined,
    level: "h2",
    headingsTheme,
    fonts: currentFonts,
  });
  const resolvedTitleFontSize = resolveHeadingFontSize({
    override: titleFontSize,
    level: "h2",
    headingsTheme,
  });
  const resolvedIconColor = resolveBodyColor({
    override: color,
    bodyTheme,
    palette: currentColorPalette,
  });
  const titleSizeClasses = extractUtilityClasses(titleFontSize, "text-");
  const titleWeightClasses = extractUtilityClasses(titleFontWeight, "font-");
  if (!isVisible) return null;
  return (
    <div>
      {title ? (
        <h3
          className={`${titleWeightClasses || "font-semibold"} mb-4`}
          style={{
            color: resolvedTitleColor,
            fontFamily: resolvedTitleFontFamily,
            fontSize: titleSizeClasses
              ? undefined
              : (toCssLength(titleFontSize) ?? resolvedTitleFontSize),
          }}
        >
          {title}
        </h3>
      ) : null}
      <div className="flex flex-wrap gap-4">
        {normalizeSocialIcons(socialIcons).map((social, idx) => (
          <a
            key={`${social.url}-${idx}`}
            href={safeHref(social.url)}
            target="_blank"
            rel="noopener noreferrer"
            className="transition-colors duration-200"
            style={{ color: resolvedIconColor }}
          >
            {iconForPlatform(social.platform, resolvedIconColor, size ?? 24)}
          </a>
        ))}
      </div>
    </div>
  );
}

export function FooterContact({
  // DEFAULT FALSE, matching the gate in FooterSection below.
  //
  // This component is registered in `resolver.ts`, so a baked page JSON can
  // render it directly without going through that gate — a default of `true`
  // here was a second opinion on the same question, and the one that publishes.
  // A page that never chose to show contact details must not show them, and
  // the placeholders it would show (info@company.com, +1 (123) 456-7890,
  // 123 Street Name) are clickable mailto:/tel: links on a live event site.
  // A page that DID choose carries the flag explicitly and is unaffected.
  isVisible = false,
  title,
  email,
  phone,
  address,
  titleColor,
  titleFontSize,
  titleFontWeight,
  contactColor,
}: {
  isVisible?: boolean;
  title?: string;
  email?: string;
  phone?: string;
  address?: string;
  titleColor?: string;
  titleFontSize?: string;
  titleFontWeight?: string;
  contactColor?: string;
}) {
  const { currentColorPalette, currentFonts, bodyTheme, headingsTheme } =
    useWBTheme();
  const resolvedTitleColor = resolveHeadingColor({
    override: titleColor,
    level: "h2",
    headingsTheme,
    palette: currentColorPalette,
  });
  const resolvedTitleFontFamily = resolveHeadingFontFamily({
    override: undefined,
    level: "h2",
    headingsTheme,
    fonts: currentFonts,
  });
  const resolvedTitleFontSize = resolveHeadingFontSize({
    override: titleFontSize,
    level: "h2",
    headingsTheme,
  });
  const resolvedContactColor = resolveBodyColor({
    override: contactColor,
    bodyTheme,
    palette: currentColorPalette,
  });
  const titleWeightClasses = extractUtilityClasses(titleFontWeight, "font-");
  if (!isVisible) return null;
  return (
    <div className="mt-6">
      {title ? (
        <h3
          className={`${titleWeightClasses || "font-semibold"} mb-4`}
          style={{
            color: resolvedTitleColor,
            fontFamily: resolvedTitleFontFamily,
            fontSize: resolvedTitleFontSize,
          }}
        >
          {title}
        </h3>
      ) : null}
      {email ? (
        <div
          className="flex items-center mb-2"
          style={{ color: resolvedContactColor }}
        >
          <IconMail size={16} className="mr-2 shrink-0" />
          <a
            href={`mailto:${email}`}
            className="whitespace-nowrap text-ellipsis overflow-hidden"
            style={{ color: "inherit" }}
          >
            {email}
          </a>
        </div>
      ) : null}
      {phone ? (
        <div
          className="flex items-center mb-2"
          style={{ color: resolvedContactColor }}
        >
          <IconPhone size={16} className="mr-2 shrink-0" />
          <a
            href={`tel:${phone}`}
            className="whitespace-nowrap"
            style={{ color: "inherit" }}
          >
            {phone}
          </a>
        </div>
      ) : null}
      {address ? (
        <div
          className="flex items-start mb-2"
          style={{ color: resolvedContactColor }}
        >
          <IconMapPin size={16} className="mr-2 mt-1 shrink-0" />
          <span>{address}</span>
        </div>
      ) : null}
    </div>
  );
}

export function FooterCopyright({
  isVisible = true,
  companyName,
  color,
  fontSize,
  alignment = "center",
}: {
  isVisible?: boolean;
  companyName?: string;
  color?: string;
  fontSize?: string;
  alignment?: "left" | "center" | "right";
}) {
  const { currentColorPalette, bodyTheme, hasWhiteLabelAccess } = useWBTheme();
  const resolvedColor = resolveBodyColor({
    override: color,
    bodyTheme,
    palette: currentColorPalette,
  });
  const copyrightSizeClasses = extractUtilityClasses(fontSize, "text-");
  const currentYear = new Date().getFullYear();

  if (!isVisible) return null;
  return (
    <p
      className={copyrightSizeClasses || undefined}
      style={{
        marginBottom: 0,
        color: resolvedColor,
        fontSize: toCssLength(fontSize),
        textAlign: alignment,
      }}
    >
      &copy; {currentYear}{" "}
      {hasWhiteLabelAccess ? (
        <>{nonEmptyString(companyName)?.trim() ?? "Your Company"}. All rights reserved.</>
      ) : (
        <>
          <a
            href="https://veltric.app"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: resolvedColor, textDecoration: "underline" }}
          >
            Veltric Guest Solutions
          </a>
          . All rights reserved.
        </>
      )}
    </p>
  );
}

/**
 * Does this footer show a contact block at all?
 *
 * Extracted so the rule is testable: `FooterSection` renders through
 * `<Element>`, which needs the engine's Editor/Node context that the runtime
 * component tests deliberately do not set up (see testimonials.test.tsx), so
 * the wrapper itself cannot be driven here. The decision can be.
 *
 * Absent = NO. The flag was only ever written by the template seed, which set
 * it true alongside placeholder values, and the builder offers no switch — so
 * a page that never chose must not publish.
 */
export function showsContactBlock(props: RuntimeProps): boolean {
  return Boolean(props.isContactVisible ?? false)
}

export function FooterSection(props: RuntimeProps) {
  const { currentColorPalette, currentFonts, currentRounding } = useWBTheme();

  const border = (props.border as boolean | undefined) ?? false;
  const borderRadius = props.borderRadius as string | undefined;
  const sectionHeight = props.sectionHeight as number | undefined;
  const contentAlignment =
    (props.contentAlignment as "top" | "center" | "bottom" | undefined) ??
    "center";
  const contentAlign =
    (props.contentAlign as "left" | "center" | "right" | undefined) ?? "left";
  const padding =
    (props.padding as string | undefined) ?? "px-4 py-6 wb-md:px-5 wb-lg:px-6";
  const sectionMargin = (props.sectionMargin as string | undefined) ?? "py-0";

  const borderClasses = getBorderClasses(
    border,
    props.borderWidth as string | undefined,
    props.borderStyle as string | undefined,
    props.borderSides as BorderSide[] | undefined,
  );
  const roundedClasses = extractUtilityClasses(
    String(borderRadius ?? currentRounding),
    "rounded",
  );
  const alignmentClass =
    contentAlignment === "center"
      ? "justify-center"
      : contentAlignment === "bottom"
        ? "justify-end"
        : "justify-start";
  const contentAlignClasses =
    contentAlign === "center"
      ? "items-center"
      : contentAlign === "right"
        ? "items-end"
        : "items-start";
  const defaultSectionOrder: Array<"logo" | "links" | "social"> = [
    "logo",
    "links",
    "social",
  ];
  const footerSectionOrder =
    (props.sectionOrder as Array<"logo" | "links" | "social"> | undefined) ??
    defaultSectionOrder;

  if (!isVisible(props.isVisible as boolean | undefined)) return null;

  return (
    <div
      className={classNames(
        "websitebuilder w-full flex justify-center",
        sectionMargin,
      )}
    >
    <footer
      className={classNames(
        padding,
        borderClasses || undefined,
        roundedClasses || undefined,
        "w-full flex flex-col",
        alignmentClass,
      )}
      style={{
        backgroundColor: String(
          props.backgroundColor ?? currentColorPalette.background,
        ),
        fontFamily: currentFonts.body,
        borderColor: border
          ? String(props.borderColor ?? currentColorPalette.secondary)
          : undefined,
        minHeight: sectionMinHeight(sectionHeight),
      }}
    >
      <div className="max-w-275 mx-auto">
        <div
          className={classNames(
            "flex flex-col wb-md:flex-row wb-md:justify-between flex-wrap gap-6 mb-6",
            contentAlignClasses,
            "wb-md:items-start",
          )}
        >
          {footerSectionOrder.map((sectionId) => {
            switch (sectionId) {
              case "logo":
                return (
                  <div key="logo" className="shrink-0">
                    <Element
                      is={AppBarLogo}
                      id="logo"
                      isVisible={Boolean(props.isLogoVisible ?? true)}
                      src={String(props.logoSrc ?? "")}
                      width={props.logoWidth as string | undefined}
                      height={(props.logoHeight as string | undefined) ?? "h-auto"}
                    />
                  </div>
                );
              case "links":
                return (
                  <div
                    key="links"
                    className={classNames(
                      "shrink-0",
                      props.stripped ? "hidden" : undefined,
                    )}
                  >
                    <Element
                      is={FooterLinks}
                      id="links"
                      isVisible={Boolean(props.isLinkGroupsVisible ?? true)}
                      linkGroups={
                        props.linkGroups as
                          | Array<{
                              title: string;
                              links: Array<{
                                label: string;
                                url: string;
                                linkType?: "internal" | "external";
                                isVisible?: boolean;
                                visibilityFilter?: ParentFilter;
                              }>;
                            }>
                          | undefined
                      }
                      titleColor={props.linksTitleColor as string | undefined}
                      titleFontSize={
                        props.linksTitleFontSize as string | undefined
                      }
                      titleFontWeight={
                        props.linksTitleFontWeight as string | undefined
                      }
                      linksColor={props.linksColor as string | undefined}
                      linksFontSize={props.linksFontSize as string | undefined}
                      linksFontWeight={
                        props.linksFontWeight as string | undefined
                      }
                      linksHoverColor={
                        props.linksHoverColor as string | undefined
                      }
                    />
                  </div>
                );
              case "social":
                return (
                  <div key="social" className="shrink-0">
                    <Element
                      is={FooterSocial}
                      id="social"
                      isVisible={Boolean(props.isSocialVisible ?? true)}
                      socialIcons={
                        props.socialIcons as
                          | Array<{ platform: string; url: string }>
                          | undefined
                      }
                      title={props.socialTitle as string | undefined}
                      color={props.socialColor as string | undefined}
                      size={props.socialSize as number | undefined}
                      titleColor={props.linksTitleColor as string | undefined}
                      titleFontSize={
                        props.linksTitleFontSize as string | undefined
                      }
                      titleFontWeight={
                        props.linksTitleFontWeight as string | undefined
                      }
                    />
                  </div>
                );
              default:
                return null;
            }
          })}
        </div>

        <div className="pt-6">
          <Element
            is={FooterCopyright}
            id="copyright"
            isVisible={Boolean(props.isCopyrightVisible ?? true)}
            companyName={props.copyrightCompanyName as string | undefined}
            color={props.copyrightColor as string | undefined}
            fontSize={props.copyrightFontSize as string | undefined}
            alignment={
              props.alignment as
                | "left"
                | "center"
                | "right"
                | undefined
            }
          />
        </div>

        {/* H40: this slot was DOUBLE-killed — wrapped in `hidden` AND fed
            `contactTitle`/`contactEmail`/… while FooterContact destructures
            `title`/`email`/`phone`/`address`, so unhiding it alone would have
            rendered an empty block. Both halves are fixed here; the component
            still gates itself on `isVisible`, which is what keeps an
            unconfigured footer from growing an empty contact section.
            `contactColor` keeps its name — that one IS the component's prop. */}
        {/* The WRAPPER is gated too, not just the component. `FooterContact`
            returns null when hidden, but this `pt-6` box did not — so every
            footer without a contact block carried 24px of dead space whose
            origin was invisible in the builder. */}
        {showsContactBlock(props) && (
        <div className="pt-6">
          <Element
            is={FooterContact}
            id="contact"
            isVisible={Boolean(props.isContactVisible ?? false)}
            title={props.contactTitle as string | undefined}
            email={props.contactEmail as string | undefined}
            phone={props.contactPhone as string | undefined}
            address={props.contactAddress as string | undefined}
            titleColor={props.linksTitleColor as string | undefined}
            titleFontSize={props.linksTitleFontSize as string | undefined}
            titleFontWeight={props.linksTitleFontWeight as string | undefined}
            contactColor={props.contactColor as string | undefined}
          />
        </div>
        )}
      </div>
    </footer>
    </div>
  );
}
